import { useState, useEffect } from "react";
import { User, Users, Wifi, Snowflake, Bath, Tv, Briefcase, ArrowUpRight, Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const RoomCard = ({ room }) => {
  const { t } = useLang();
  const Icon = room.icon;
  const images = room.images || [room.image];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length < 2) return;
    const timer = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [images.length]);

  const prev = (e) => { e.stopPropagation(); setIdx((i) => (i - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setIdx((i) => (i + 1) % images.length); };

  return (
    <article
      className="room-card rounded-2xl overflow-hidden flex flex-col"
      data-testid={`room-card-${room.id}`}
    >
      <div className="relative h-[260px] overflow-hidden group/img">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={t(room.titleKey)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              data-testid={`room-${room.id}-prev`}
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-[#0b0c10]/70 border border-[rgba(0,255,102,0.35)] hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.15)] transition-all flex items-center justify-center opacity-0 group-hover/img:opacity-100"
            >
              <ChevronLeft size={16} className="text-[#00FF66]" />
            </button>
            <button
              onClick={next}
              data-testid={`room-${room.id}-next`}
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-[#0b0c10]/70 border border-[rgba(0,255,102,0.35)] hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.15)] transition-all flex items-center justify-center opacity-0 group-hover/img:opacity-100"
            >
              <ChevronRight size={16} className="text-[#00FF66]" />
            </button>
            <div className="absolute top-3 right-3 z-10 flex gap-1.5">
              {images.map((_, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                  aria-label={`Go to image ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#00FF66]" : "w-1.5 bg-white/40"}`}
                />
              ))}
            </div>
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c10] via-[#0b0c10]/20 to-transparent" />
        <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#0b0c10]/80 border border-[rgba(0,255,102,0.35)] text-[#00FF66] text-[10px] font-heading tracking-widest uppercase">
          {room.type}
        </div>
        <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
          <div>
            <div className="text-[10px] text-[#9CA3AF] font-heading tracking-widest uppercase">
              {t(room.availKey)}
            </div>
            <h3 className="font-heading text-3xl font-extrabold text-white mt-1">
              {t(room.titleKey)}
            </h3>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-white bg-[rgba(0,255,102,0.15)] border border-[rgba(0,255,102,0.35)] px-3 py-1.5 rounded-full">
            <Icon size={14} className="text-[#00FF66]" />
            <span>{t(room.capKey)}</span>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8 flex-1 flex flex-col">
        {/* 3-Tier Pricing */}
        <div className="pb-5 border-b border-white/5">
          <div className="text-[#9CA3AF] text-[10px] font-heading uppercase tracking-[0.25em] mb-3">
            {t("room_choose_plan")}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {room.tiers.map((tier) => {
              const isBest = tier.best;
              const waMsg = `${t("room_wa_msg")} ${t(room.titleKey)} paket ${tier.name} (${t(tier.subKey)}) — IDR ${tier.price}K.`;
              return (
                <a
                  key={tier.id}
                  href={`https://wa.me/6285640170705?text=${encodeURIComponent(waMsg)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`room-${room.id}-tier-${tier.id}`}
                  aria-label={`Book ${t(room.titleKey)} ${tier.name} via WhatsApp`}
                  className={`relative rounded-xl p-3 text-center transition-all cursor-pointer block group/tier ${
                    isBest
                      ? "bg-[rgba(0,255,102,0.12)] border border-[#00FF66] shadow-[0_0_20px_-6px_rgba(0,255,102,0.55)] hover:bg-[rgba(0,255,102,0.2)] hover:shadow-[0_0_28px_-4px_rgba(0,255,102,0.75)]"
                      : "bg-[rgba(255,255,255,0.02)] border border-white/8 hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.08)]"
                  } hover:-translate-y-0.5`}
                >
                  {isBest && (
                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#00FF66] text-[#0b0c10] text-[8px] font-heading font-extrabold tracking-widest whitespace-nowrap">
                      BEST DEAL
                    </span>
                  )}
                  <div
                    className={`text-[9px] font-heading font-bold tracking-widest uppercase mb-1 ${
                      isBest ? "text-[#00FF66]" : "text-white/70 group-hover/tier:text-[#00FF66]"
                    }`}
                  >
                    {tier.name}
                  </div>
                  <div className="text-[9px] text-[#9CA3AF] leading-tight mb-2 min-h-[22px]">
                    {t(tier.subKey)}
                  </div>
                  <div className="flex items-baseline justify-center gap-0.5">
                    <span
                      className={`font-heading font-extrabold text-xl ${
                        isBest ? "text-[#00FF66]" : "text-white group-hover/tier:text-[#00FF66]"
                      }`}
                    >
                      {tier.price}
                    </span>
                    <span className="text-[9px] text-[#9CA3AF]">K</span>
                  </div>
                  <div
                    className={`mt-2 text-[8px] font-heading tracking-widest uppercase transition-opacity ${
                      isBest ? "text-[#00FF66]/90" : "text-[#00FF66]/0 group-hover/tier:text-[#00FF66]/90"
                    }`}
                  >
                    Book →
                  </div>
                </a>
              );
            })}
          </div>
        </div>

        <p className="mt-5 text-[#9CA3AF] leading-relaxed text-sm">
          {t(room.descKey)}
        </p>

        <ul className="mt-6 space-y-2.5 flex-1">
          {room.amenityKeys.map((k, idx) => (
            <li key={idx} className="flex items-center gap-3 text-sm text-white/85">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[rgba(0,255,102,0.1)] border border-[rgba(0,255,102,0.3)]">
                <Check size={12} className="text-[#00FF66]" />
              </span>
              {t(k)}
            </li>
          ))}
        </ul>

        <a
          href={`https://wa.me/6285640170705?text=${encodeURIComponent(
            `${t("room_wa_msg")} ${t(room.titleKey)}.`
          )}`}
          target="_blank"
          rel="noreferrer"
          data-testid={`room-cta-${room.id}`}
          className="mt-8 inline-flex items-center justify-between w-full px-5 py-3.5 rounded-full border border-[rgba(0,255,102,0.35)] hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.08)] transition-all group"
        >
          <span className="font-heading tracking-wider text-sm uppercase text-white">
            {t("room_book_cta")}
          </span>
          <ArrowUpRight
            size={18}
            className="text-[#00FF66] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
      </div>
    </article>
  );
};

const Rooms = () => {
  const { t } = useLang();
  // tierPrices shape: { single: { flexytime, flexy24, flexyregular }, double: { ... } }
  const [tierPrices, setTierPrices] = useState(null);

  useEffect(() => {
    const dbUrl = process.env.REACT_APP_FIREBASE_DB_URL;
    if (!dbUrl) return;
    const controller = new AbortController();

    // Normalize a Firebase value into a display string like "148" (kept in "K" units)
    const toDisplay = (v) => {
      if (v === null || v === undefined) return null;
      if (typeof v === "number") {
        // If someone entered 148000, convert back to "148"
        const n = v >= 10000 ? Math.round(v / 1000) : v;
        return String(n);
      }
      const s = String(v).trim();
      if (/^\d+$/.test(s)) {
        const n = Number(s);
        const v2 = n >= 10000 ? Math.round(n / 1000) : n;
        return String(v2);
      }
      return s;
    };

    const normKey = (k) => k.trim().toLowerCase();

    fetch(`${dbUrl}/prices.json`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || typeof data !== "object") return;

        const out = {};
        Object.keys(data).forEach((roomKey) => {
          const room = normKey(roomKey);
          const raw = data[roomKey];
          if (raw && typeof raw === "object") {
            // Nested tier structure: {flexytime, flexy24, flexyregular}
            const t = {};
            Object.keys(raw).forEach((tk) => {
              const price = toDisplay(raw[tk]);
              if (price !== null) t[normKey(tk)] = price;
            });
            out[room] = t;
          }
        });
        if (Object.keys(out).length) setTierPrices(out);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const priceFor = (roomId, tierId, fallback) => {
    const p = tierPrices?.[roomId]?.[tierId];
    return p ?? fallback;
  };

  const rooms = [
    {
      id: "single",
      titleKey: "room_single_title",
      type: "Type 1",
      availKey: "room_available_single",
      tiers: [
        { id: "flexytime", name: "FLEXYTIME", subKey: "tier_flexytime_sub", price: priceFor("single", "flexytime", "148") },
        { id: "flexy24", name: "FLEXY24", subKey: "tier_flexy24_sub", price: priceFor("single", "flexy24", "228"), best: true },
        { id: "flexyregular", name: "FLEXYREGULAR", subKey: "tier_flexyregular_sub", price: priceFor("single", "flexyregular", "188") },
      ],
      capKey: "room_capacity_single",
      icon: User,
      images: [
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/uyhu6wtg_WhatsApp%20Image%202026-07-05%20at%203.07.01%20PM%20%282%29.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/k4mu4gv1_WhatsApp%20Image%202026-07-05%20at%203.06.45%20PM.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/4xy60ngz_WhatsApp%20Image%202026-07-05%20at%203.07.00%20PM.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/f7e996jx_WhatsApp%20Image%202026-07-05%20at%203.07.00%20PM%20%281%29.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/7dujisy9_WhatsApp%20Image%202026-07-05%20at%203.07.00%20PM%20%282%29.jpeg",
      ],
      descKey: "room_desc_single",
      amenityKeys: [
        "room_amenity_single_1",
        "room_amenity_single_2",
        "room_amenity_single_3",
        "room_amenity_single_4",
      ],
    },
    {
      id: "double",
      titleKey: "room_double_title",
      type: "Type 2",
      availKey: "room_available_double",
      tiers: [
        { id: "flexytime", name: "FLEXYTIME", subKey: "tier_flexytime_sub", price: priceFor("double", "flexytime", "188") },
        { id: "flexy24", name: "FLEXY24", subKey: "tier_flexy24_sub", price: priceFor("double", "flexy24", "248"), best: true },
        { id: "flexyregular", name: "FLEXYREGULAR", subKey: "tier_flexyregular_sub", price: priceFor("double", "flexyregular", "228") },
      ],
      capKey: "room_capacity_double",
      icon: Users,
      images: [
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/jjn2ykar_WhatsApp%20Image%202026-07-05%20at%203.06.45%20PM%20%281%29.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/1oi75m7c_WhatsApp%20Image%202026-07-05%20at%203.06.44%20PM.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/y4m2fxnr_WhatsApp%20Image%202026-07-05%20at%203.06.46%20PM.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/pabcueq4_WhatsApp%20Image%202026-07-05%20at%203.06.45%20PM%20%282%29.jpeg",
        "https://customer-assets.emergentagent.com/job_lokabeds-footer/artifacts/94iyxsha_WhatsApp%20Image%202026-07-05%20at%203.06.50%20PM.jpeg",
      ],
      descKey: "room_desc_double",
      amenityKeys: [
        "room_amenity_double_1",
        "room_amenity_double_2",
        "room_amenity_double_3",
        "room_amenity_double_4",
        "room_amenity_double_5",
      ],
    },
  ];

  // Suppress unused import warnings (icons imported for lucide usage in future)
  void Wifi; void Snowflake; void Bath; void Tv; void Briefcase;

  return (
    <section
      id="rooms"
      data-testid="rooms-section"
      className="relative py-24 lg:py-32 bg-[#0b0c10]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs font-heading tracking-[0.3em] text-[#00FF66] uppercase mb-4">
              {t("rooms_kicker")}
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-white leading-[1.05]">
              {t("rooms_title_1")}
              <br />
              {t("rooms_title_2")}
            </h2>
          </div>
          <p className="text-[#9CA3AF] max-w-md text-sm sm:text-base">
            {t("rooms_lead")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {rooms.map((r) => (
            <RoomCard key={r.id} room={r} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Rooms;
